// src/components/Board/KanbanBoard.jsx
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  openCreateModal,
  selectGroupedTasks,
  selectAllTasks,
  selectFilteredTasks,
} from '../../store/slices/tasksSlice.js';
import { clearAllFilters, selectFilters } from '../../store/slices/filtersSlice.js';
import { TASK_STATUS_ORDER, TASK_STATUS_LABELS } from '../../constants/task.js';
import { hasActiveFilters } from '../../utils/taskUtils.js';
import { useTaskBoard } from '../../hooks/useTaskBoard.js';
import TaskCard from '../TaskCard/TaskCard.jsx';

const STATUS_DOTS = {
  backlog: '#5c5c72',
  in_progress: '#5ba4f5',
  review: '#e8a838',
  done: '#4ade80',
};

function Column({ status, tasks, dragOverCol, onDragOver, onDrop, onDragLeave, onDragStart }) {
  const dispatch = useDispatch();
  const isDragOver = dragOverCol === status;
  const label = TASK_STATUS_LABELS[status];

  return (
    <div className="column" aria-label={`${label} column, ${tasks.length} tasks`}>
      <div className="col-header">
        <div className="col-title">
          <div className="col-dot" style={{ background: STATUS_DOTS[status] }} aria-hidden="true" />
          {label}
          <span className="col-count" aria-label={`${tasks.length} tasks`}>{tasks.length}</span>
        </div>
        <button
          className="btn btn-ghost btn-icon btn-sm"
          onClick={() => dispatch(openCreateModal())}
          title={`Add task to ${label}`}
          aria-label={`Add task to ${label}`}
          type="button"
        >
          +
        </button>
      </div>

      <div
        className={`col-body${isDragOver ? ' drag-over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); onDragOver(status); }}
        onDrop={(e) => onDrop(e, status)}
        onDragLeave={onDragLeave}
        role="list"
        aria-dropeffect="move"
        aria-label={`${label} tasks`}
      >
        {isDragOver && (
          <div className="drop-placeholder" aria-hidden="true">
            Drop here → {label}
          </div>
        )}

        {tasks.length === 0 && !isDragOver && (
          <div className="col-empty" aria-label="No tasks in this column">
            No tasks
          </div>
        )}

        {!isDragOver && tasks.map((task) => (
          <div key={task.id} role="listitem">
            <TaskCard task={task} onDragStart={onDragStart} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function KanbanBoard() {
  const dispatch = useDispatch();
  const grouped = useSelector(selectGroupedTasks);
  const allTasks = useSelector(selectAllTasks);
  const filteredTasks = useSelector(selectFilteredTasks);
  const filters = useSelector(selectFilters);
  const {
    dragOverCol,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragLeave,
  } = useTaskBoard();

  // Empty board
  if (allTasks.length === 0) {
    return (
      <div className="board-empty">
        <div className="board-empty-icon" aria-hidden="true">📋</div>
        <h3>No tasks yet</h3>
        <p>Create your first task to get started</p>
        <button
          className="btn btn-primary"
          onClick={() => dispatch(openCreateModal())}
          type="button"
        >
          + New Task
        </button>
      </div>
    );
  }

  // Filters active but zero results
  const totalFiltered = TASK_STATUS_ORDER.reduce((s, k) => s + grouped[k].length, 0);
  if (hasActiveFilters(filters) && totalFiltered === 0) {
    return (
      <div className="board-empty">
        <div className="board-empty-icon" aria-hidden="true">🔍</div>
        <h3>No matching tasks</h3>
        <p>Try adjusting your search or filters</p>
        <button
          className="btn"
          onClick={() => dispatch(clearAllFilters())}
          type="button"
        >
          Clear all filters
        </button>
      </div>
    );
  }

  return (
    <main className="board-wrap">
      <div className="board" role="region" aria-label="Task board">
        {TASK_STATUS_ORDER.map((status) => (
          <Column
            key={status}
            status={status}
            tasks={grouped[status]}
            dragOverCol={dragOverCol}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragLeave={handleDragLeave}
          />
        ))}
      </div>
    </main>
  );
}
